import React from 'react';
import { useTranslation } from 'react-i18next';
import { RoleTaskFull } from 'interfaces/roleTask.interface';
import { Ability, ShortUser, User } from '@umanforge/common';
import { MultiSelect } from '@umanforge/common';
import Counter from 'components/inputs/counter';
import { UserIcon } from '@common_components/icons/user.icon';
import { CrossIcon } from '@common_components/icons/cross.icon';
import Button from '@common_components/buttons/button.component';
import { ILUOSkill } from 'components/inputs/iluo_skill.component';
import { useRoleTaskTileForm } from '../hook/roleTaskTileForm.hook';
import { SkillRT } from 'interfaces/roleTaskSkill.interface';

interface RoleTaskTileFormProps {
  onChange: ( roleTask: RoleTaskFull, index: number ) => void;
  onDelete: ( roleTask: RoleTaskFull, index: number ) => void;
  roleTask: RoleTaskFull;
  index: number;
  sectorUsers: ShortUser[];
  skills: SkillRT[];
  abilities: Ability[];
  isAdmin: boolean;
  fixed?: boolean;
  onILUOUpdate?: ( skillId: string, iluo: number ) => number;
  canClose?: boolean;
}

const RoleTaskTileForm: React.FC<RoleTaskTileFormProps> = ( {
  onChange,
  onDelete,
  roleTask,
  index,
  sectorUsers,
  skills = [],
  abilities = [],
  isAdmin = false,
  fixed = false,
  onILUOUpdate = ( skillId: string, iluo:number ) => { return iluo; },
  canClose = true
} ) => {
  const { t } = useTranslation();

  const {
    skillOptions,
    abilityOptions,
    userOptions,
    count, setCount,
    selectedAbilities, setSelectedAbilities,
    selectedSkills, setSelectedSkills,
    selectedUsers, setSelectedUsers,
    errors
  } = useRoleTaskTileForm( onChange, onDelete, roleTask, sectorUsers, index, skills, abilities, fixed );

  // Render
  return (
    <div className='rounded-md shadow-md border mb-2'>
      <div className='flex w-full -mr-3'>
        <p className='text-gray-600 left px-5 pt-2 w-full'>
          {`${ t( 'translation:skill_number' ) } ${ index + 1 } ${ roleTask.id == 'equipment_roletask' ? t( 'translation:equipment_roletask' ) : ''}`}
        </p>
        { canClose && !fixed &&
          <Button
            icon={ <CrossIcon size={5} /> }
            color="bg-transparent"
            colorHover="hover:bg-red-200"
            textColorHover="hover:text-red-base"
            hoverBorderColor="hover:border-transparent"
            textSize="text-md"
            roundedRadius='rounded-xl'
            shadow="hover:shadow-md"
            onClick={() => { onDelete( roleTask, index ); }}
            disable={fixed}
            margin='m-2'
            padding='p-0.5'
          />
        }
      </div>
      <div className='pb-5 px-5 flex flex-col w-full'>
        <div className={'lg:flex lg:gap-4 mb-2'}>
          <div className='place-self-start lg:place-self-center max-lg:-mt-2 max-lg:mb-2'>
            <Counter
              id={`counter_${roleTask.id}_${index}`}
              value={ count }
              max={5}
              min={1}
              setValue={ setCount }
              icon={<UserIcon size={7} className='place-self-center' />}
              vertical
              disable={fixed}
              textable
            />
          </div>


          <MultiSelect
            label={ t( 'common:authorizations' ) }
            disabled={ fixed || !isAdmin || abilityOptions.length == 0 }
            placeholder={ t( 'translation:no_associated_authorizations' ) }
            getValues={ abilityOptions }
            selectedValues={ selectedAbilities }
            clearable={ false }
            onChange={ newAbilities => {
              setSelectedAbilities(
                newAbilities.sort( ( item1, item2 ) => {
                  return item1.object.name.toUpperCase() < item2.object.name.toUpperCase() ? -1 : 1;
                } ) );
            } }
            extra={ability => {
              return (
                <div>{ ( ability as unknown as Ability ).name }</div>
              );
            } }
          />

          <MultiSelect
            label={ t( 'common:skills' ) }
            disabled={ fixed || !isAdmin || skillOptions.length == 0 }
            placeholder={ t( 'translation:no_associated_skills' ) }
            getValues={ skillOptions }
            selectedValues={ selectedSkills }
            clearable={ false }
            onChange={
              newSkills => {
                setSelectedSkills(
                  newSkills.sort( ( item1, item2 ) => {
                    return item1.object.name.toUpperCase() < item2.object.name.toUpperCase() ? -1 : 1;
                  } ) );
              } }
            extra={skill => {
              return (
                <ILUOSkill
                  skill={ ( { ... skill.object, value: skill.object.iluo } ) }
                  onChange={ changedSkill => {
                    const foundSkill = selectedSkills.find( item => { return changedSkill.id == item.object.id;  } );
                    if ( foundSkill ) {
                      let iluo = changedSkill.value;
                      if ( roleTask.id === 'equipment_roletask' ) {
                        iluo = onILUOUpdate( changedSkill.id, changedSkill.value );
                      }
                      foundSkill.object.iluo = iluo;
                    }
                    setSelectedSkills(
                      structuredClone( selectedSkills )
                        .sort( ( item1, item2 ) => {
                          const value1 = item1.object.value ?? item1.object.iluo ?? 1;
                          const value2 = item2.object.value ?? item1.object.iluo ?? 1;
                          if ( value1 === value2 ) {
                            return item1.object.name.toUpperCase() < item2.object.name.toUpperCase() ? -1 : 1;
                          }
                          return ( value1 < value2 ) ? -1 : 1;
                        } )
                    );
                  } }
                />
              );
            } }
          />
        </div>

        <MultiSelect
          label={ t( 'common:users' ) }
          disabled={ fixed || !isAdmin || userOptions.length == 0 }
          placeholder={ t( 'translation:no_associated_users' ) }
          getValues={ userOptions }
          selectedValues={ selectedUsers }
          clearable={ false }
          onChange={ newUsers => {
            if ( newUsers.length <= count ) {
              setSelectedUsers(
                newUsers.sort( ( item1, item2 ) => {
                  return item1.object.fullname.toUpperCase() < item2.object.fullname.toUpperCase() ? -1 : 1;
                } )
              );
            }
            else {
              setSelectedUsers( [ ...selectedUsers ] );
            }
          } }
          extra={user => {
            return (
              <div>{ ( user as unknown as User ).lastname }</div>
            );
          } }
        />
        { errors?.users && errors?.users.length > 0 && errors?.users.map( ( user, idx ) => {
          return <p key={ 'user_error_' + idx + '_' + user
          } className="text-red-500 h-6 my-2">{ t( 'translation:errors.users_doesnt_have_authorizations_skills_to_replace_user', { userName: user } ) }</p>;
        } ) }
      </div>
    </div>
  );
};

export default RoleTaskTileForm;
